import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Lock, Palette, CreditCard, Trash2 } from 'lucide-react';
import { useUserProfile, useUpdateUserProfile, useSignOutAll } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

export function AccountSettingsScreen() {
  const { profile, loading } = useUserProfile();
  const { updateProfile, loading: saving } = useUpdateUserProfile();
  const signOutAll = useSignOutAll();
  const { toast } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [locale, setLocale] = useState('');
  const [dateFormat, setDateFormat] = useState('');

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName ?? '');
      setLastName(profile.lastName ?? '');
      setTimezone(profile.timezone ?? '');
      setLocale(profile.locale ?? '');
      setDateFormat(profile.dateFormat ?? '');
    }
  }, [profile]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await updateProfile({
        firstName: firstName || null,
        lastName: lastName || null,
        timezone: timezone || undefined,
        locale: locale || undefined,
        dateFormat: dateFormat || undefined,
      });
      toast({ title: 'Profile saved', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-72 rounded-xl" />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader title="Account" description="Your profile, locale, and security." />
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Field label="Email" htmlFor="email" hint="Contact support to change.">
                <Input id="email" value={profile?.email ?? ''} disabled />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="First name" htmlFor="firstName">
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Field>
                <Field label="Last name" htmlFor="lastName">
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Timezone" htmlFor="timezone">
                  <Input
                    id="timezone"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  />
                </Field>
                <Field label="Locale" htmlFor="locale">
                  <Input
                    id="locale"
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    placeholder="e.g. en-US"
                  />
                </Field>
                <Field label="Date format" htmlFor="dateFormat">
                  <Input
                    id="dateFormat"
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    placeholder="MM/DD/YYYY"
                  />
                </Field>
              </div>
              <div className="flex justify-end">
                <Button type="submit" loading={saving}>
                  Save profile
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Personalisation</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button variant="outline" asChild>
                <Link to="/settings/theme">
                  <Palette className="size-4" /> App theme
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/settings/payments">
                  <CreditCard className="size-4" /> Online payments
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/settings/design">
                  <FileText className="size-4" /> Invoice design
                </Link>
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Security</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button variant="outline" asChild>
                <Link to="/settings/change-password">
                  <Lock className="size-4" /> Change password
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Sign out of every other device?')) return;
                  try {
                    const res = await signOutAll.signOutAll();
                    toast({
                      title: 'Other sessions revoked',
                      description: `Signed out of ${res.revoked} device(s).`,
                      variant: 'success',
                    });
                  } catch (err) {
                    const desc = describeAppError(err);
                    toast({
                      title: desc.title,
                      description: desc.message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                Sign out other devices
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Danger zone</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild className="text-destructive hover:bg-destructive/10 hover:text-destructive">
                <Link to="/settings/delete-account">
                  <Trash2 className="size-4" /> Delete account
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}
